/**
 * BatchPreview - Auto-batch preview display component
 *
 * Displays the auto-batch composition with:
 * 1. Summary card (total orders, items, POD/P&P breakdown)
 * 2. Priority items card (redos, force-included P&P)
 * 3. SKU breakdown table with pretreat indicators
 * 4. Order age range
 * 5. Confirm/Back action buttons
 *
 * Note: "Adjust Selection" button is V2 deferred - not implemented in V1
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Package,
  AlertCircle,
  Droplet,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import type { AutoBatchPreview } from '../types';
import { formatRelativeTime } from '../utils';
import { BatchInclusionToggle } from './BatchInclusionToggle';

// ============================================================================
// COMPONENT
// ============================================================================

interface BatchPreviewProps {
  preview: AutoBatchPreview;
  onConfirm: () => void;
  onBack: () => void;
  isCreating: boolean;
  /** Hide the action buttons at the bottom (when parent renders them) */
  hideActions?: boolean;
  /**
   * BLU-777 — the two injection gates, split (closes BLU-632). Both default true
   * (legacy). The toggles render only when BOTH change handlers are provided.
   *
   * `includeRedos` gates pending REDO garments; `includeRequeued` gates
   * priority-queued / re-routed ORDERS. Only `includeRedos` affects the garment
   * totals shown here — re-queued orders arrive with their own units, which the
   * preview does not project.
   */
  includeRedos?: boolean;
  onIncludeRedosChange?: (checked: boolean) => void;
  includeRequeued?: boolean;
  onIncludeRequeuedChange?: (checked: boolean) => void;
}

export function BatchPreview({
  preview,
  onConfirm,
  onBack,
  isCreating,
  hideActions = false,
  includeRedos = true,
  onIncludeRedosChange,
  includeRequeued = true,
  onIncludeRequeuedChange,
}: BatchPreviewProps) {
  const { t } = useTranslation('production');
  const [showAllSkus, setShowAllSkus] = useState(false);

  const { summary, skuBreakdown, papBalance, redos } = preview;

  // Live count feedback: when the operator opts out of redos, drop them from the
  // totals. Backend: summary.totalItems includes redos (orders' units + redos.length).
  // Mirror manual mode: keep the redo contribution VISIBLE but struck-through when off,
  // so the operator sees exactly what they're dropping.
  // ⚠ Keyed on `includeRedos` ALONE. `summary.totalItems` = the orders' own units
  // + redos, so turning re-queued ORDERS off changes nothing in this figure —
  // subtracting redos for it would under-report the batch.
  const hasRedoContribution = summary.totalRedoItems > 0;
  const effectiveTotalItems = includeRedos
    ? summary.totalItems
    : summary.totalItems - summary.totalRedoItems;

  // Show first 10 SKUs by default
  const displayedSkus = showAllSkus ? skuBreakdown : skuBreakdown.slice(0, 10);
  const hasMoreSkus = skuBreakdown.length > 10;

  // Priority items (redos and force-included P&P)
  const hasRedos = summary.totalRedoItems > 0;
  const hasForceIncludedPap = papBalance.forceIncluded;
  const hasPriorityItems = hasRedos || hasForceIncludedPap;

  return (
    <div className="space-y-6">
      {/* Summary Cards - 2 column grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Batch Composition Card */}
        <Card className="border-primary/20 bg-[hsl(var(--metric-production))]">
          <CardHeader className="py-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-primary">
              <Package className="h-4 w-4" />
              {t('batch.composition')}
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 space-y-3">
            {/* Total Orders */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('batch.totalOrders')}</span>
              <span className="font-medium tabular-nums">
                {summary.totalOrders}
                {hasRedoContribution && (
                  <span
                    className={`ml-1 transition-colors duration-200 ease-out motion-reduce:transition-none ${
                      includeRedos ? 'text-muted-foreground' : 'text-muted-foreground/60 line-through'
                    }`}
                  >
                    + {summary.totalRedoItems} {t('batch.redos')}
                  </span>
                )}
              </span>
            </div>

            {/* Total Items */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('batch.totalItems')}</span>
              <span className="text-lg font-semibold tabular-nums transition-colors duration-200 ease-out motion-reduce:transition-none">
                {effectiveTotalItems}
              </span>
            </div>

            {/* Order Type Breakdown */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="info" size="sm">
                POD only: {summary.podOnlyOrders}
              </Badge>
              <Badge variant="warning" size="sm">
                P&P only: {summary.papOnlyOrders}
              </Badge>
              <Badge variant="muted" size="sm">
                Mixed: {summary.mixedOrders}
              </Badge>
            </div>

            {/* Pretreat Breakdown */}
            <div className="flex justify-between items-center text-sm pt-2 border-t">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Droplet className="h-3.5 w-3.5" />
                <span>{t('batch.pretreatRequired')}</span>
              </div>
              <span className="font-medium">
                {summary.pretreatItems} garments
                <span className="text-muted-foreground ml-1">
                  ({Math.round((summary.pretreatItems / summary.totalItems) * 100) || 0}%)
                </span>
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Priority Items Card (only show if there are priority items) */}
        {hasPriorityItems && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/30">
            <CardHeader className="py-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <AlertCircle className="h-4 w-4" />
                {t('batch.priorityItems')}
              </CardTitle>
            </CardHeader>
            <CardContent className="py-3 space-y-3">
              {hasRedos && (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="destructive"
                    aria-label={`${summary.totalRedoItems} redo items prioritized at top of picklist`}
                  >
                    {summary.totalRedoItems} redos (top of picklist)
                  </Badge>
                </div>
              )}

              {hasForceIncludedPap && (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="info"
                    aria-label={`${papBalance.papOnlyWaiting} pick and pack only orders included after waiting ${papBalance.batchesSinceLastPap} batches`}
                  >
                    {papBalance.papOnlyWaiting} P&P-only (waited {papBalance.batchesSinceLastPap} batches)
                  </Badge>
                </div>
              )}

              {hasRedos && redos.length > 0 && (
                <div className="text-xs text-muted-foreground pt-2 border-t">
                  Redo SKUs: {redos.slice(0, 3).map(r => r.sku).join(', ')}
                  {redos.length > 3 && ` +${redos.length - 3} more`}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Age Range Card */}
        <Card className={!hasPriorityItems ? 'md:col-span-1' : ''}>
          <CardHeader className="py-4">
            <CardTitle className="text-sm font-medium">{t('batch.orderAgeRange')}</CardTitle>
          </CardHeader>
          <CardContent className="py-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('batch.oldest')}</span>
              <span className="font-medium">{formatRelativeTime(summary.oldestOrderAge)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('batch.newest')}</span>
              <span className="font-medium">{formatRelativeTime(summary.newestOrderAge)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* BLU-777: the two injection opt-outs, each with its own live count.
          Rendered only when the parent wires BOTH change handlers. */}
      {onIncludeRedosChange && onIncludeRequeuedChange && (
        <div className="rounded-lg border bg-card p-4">
          <BatchInclusionToggle
            id="batch-include-extras-auto"
            includeRedos={includeRedos}
            onIncludeRedosChange={onIncludeRedosChange}
            includeRequeued={includeRequeued}
            onIncludeRequeuedChange={onIncludeRequeuedChange}
            redoCount={summary.totalRedoItems}
            disabled={isCreating}
          />
        </div>
      )}

      {/* SKU Breakdown Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-sm font-medium">{t('batch.skuBreakdown')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table aria-label="SKU breakdown showing products in this batch">
            <TableHeader>
              <TableRow>
                <TableHead>{t('batch.skuColumn')}</TableHead>
                <TableHead>{t('batch.productColumn')}</TableHead>
                <TableHead className="text-right">{t('batch.ordersColumn')}</TableHead>
                <TableHead className="text-right">{t('batch.unitsColumn')}</TableHead>
                <TableHead className="text-center">{t('batch.pretreatColumn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedSkus.map((item) => (
                <TableRow key={item.sku}>
                  <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                  <TableCell className="max-w-[200px] truncate">
                    {item.productName}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {item.orderCount}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {item.unitCount}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.requiresPretreat && (
                      <Badge
                        variant="processing"
                        size="sm"
                        aria-label="Requires pretreatment"
                      >
                        <Droplet className="h-3 w-3" />
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Show More/Less Button */}
          {hasMoreSkus && (
            <div className="flex justify-center pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllSkus(!showAllSkus)}
                className="text-xs"
              >
                {showAllSkus ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    {t('batch.showLess')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    {t('batch.showAllSkus', { count: skuBreakdown.length })}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons - hidden when parent handles them */}
      {!hideActions && (
        <div className="flex justify-between items-center pt-4 border-t">
          <Button variant="outline" onClick={onBack} disabled={isCreating}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('batch.back')}
          </Button>

          {/* V2 Deferred: "Adjust Selection" button would go here */}

          <Button onClick={onConfirm} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('creating')}
              </>
            ) : (
              <>
                <Package className="h-4 w-4 mr-2" />
                {t('batch.createBatchFull', { orders: summary.totalOrders, garments: effectiveTotalItems })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
